/**
 * @fileoverview Blog section that loads markdown posts + images from `/blog/` (public assets).
 *
 * Data flow:
 * - `scripts/syncBlogAssets.js` generates `public/blog/posts.json` and copies markdown/images.
 * - This component fetches the manifest, renders a post list, and fetches markdown for detail view.
 *
 * Security:
 * - We do NOT use `dangerouslySetInnerHTML`. Markdown is parsed into React elements.
 */

import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const BlogSection = styled.section`
  padding: 5rem 0;
  background-color: var(--background-light);
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-family: "Libre Baskerville", serif;
  font-size: 2.5rem;
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-family: "Lato", sans-serif;
  font-size: 1.1rem;
  color: var(--text-dark);
  text-align: center;
  max-width: 720px;
  margin: 0 auto 3rem;
  line-height: 1.7;
`;

const TeaserHeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const TeaserTitleWrap = styled.div`
  max-width: 760px;
`;

const ViewAllLink = styled.a`
  display: inline-block;
  padding: 0.8rem 1.8rem;
  background-color: var(--accent-color);
  color: var(--text-light);
  border: none;
  border-radius: 5px;
  text-decoration: none;
  font-family: "Montserrat", sans-serif;
  font-weight: 500;
  transition: background-color 0.3s ease;
  white-space: nowrap;

  &:hover {
    background-color: var(--primary-color);
  }
`;

const HorizontalScroll = styled.div`
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  padding: 0.25rem 0.25rem 1rem;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 999px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.button`
  background-color: var(--background-white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: none;
  text-align: left;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 3px solid var(--accent-color);
    outline-offset: 3px;
  }
`;

const TeaserCard = styled(Card)`
  min-width: 320px;
  max-width: 380px;
  scroll-snap-align: start;

  @media (max-width: 576px) {
    min-width: 280px;
  }
`;

const CardImage = styled.div`
  height: 190px;
  background-color: var(--background-light);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardBody = styled.div`
  padding: 1.25rem 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-family: "Montserrat", sans-serif;
  font-size: 1.2rem;
  color: var(--primary-color);
  margin: 0;
  line-height: 1.35;
`;

const CardExcerpt = styled.p`
  font-family: "Lato", sans-serif;
  font-size: 1rem;
  color: var(--text-dark);
  margin: 0;
  line-height: 1.7;
`;

const CardCta = styled.span`
  font-family: "Montserrat", sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--accent-color);
  margin-top: auto;
`;

const DetailShell = styled.div`
  background-color: var(--background-white);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const DetailHeader = styled.div`
  padding: 1.25rem 1.25rem 0;
`;

const BackButton = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: "Montserrat", sans-serif;
  color: var(--accent-color);
  font-weight: 600;

  &:hover {
    color: var(--primary-color);
  }

  &:focus {
    outline: 3px solid var(--accent-color);
    outline-offset: 3px;
  }
`;

const DetailTitle = styled.h2`
  font-family: "Libre Baskerville", serif;
  font-size: 2rem;
  color: var(--primary-color);
  margin: 1rem 0 1rem;

  @media (max-width: 576px) {
    font-size: 1.6rem;
  }
`;

const DetailHero = styled.div`
  height: 280px;
  background-color: var(--background-light);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DetailBody = styled.div`
  padding: 1.25rem;

  p {
    font-family: "Lato", sans-serif;
    color: var(--text-dark);
    font-size: 1.05rem;
    line-height: 1.9;
    margin: 0 0 1.1rem;
  }

  h3 {
    font-family: "Montserrat", sans-serif;
    color: var(--primary-color);
    font-size: 1.3rem;
    margin: 1.5rem 0 0.75rem;
  }

  h4 {
    font-family: "Montserrat", sans-serif;
    color: var(--primary-color);
    font-size: 1.15rem;
    margin: 1.25rem 0 0.5rem;
  }

  ul,
  ol {
    padding-left: 1.25rem;
    margin: 0 0 1.1rem;
    color: var(--text-dark);
    font-family: "Lato", sans-serif;
    line-height: 1.9;
  }

  li {
    margin-bottom: 0.35rem;
  }

  hr {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    margin: 1.75rem 0;
  }

  a {
    color: var(--accent-color);
    text-decoration: none;
    font-weight: 600;
  }

  a:hover {
    color: var(--primary-color);
    text-decoration: underline;
  }
`;

const StatusText = styled.p`
  font-family: "Lato", sans-serif;
  color: var(--text-dark);
  text-align: center;
  margin: 0;
`;

/**
 * Safely renders simple inline markdown: links, **bold**, and *italics*.
 * @param {string} text
 * @returns {Array<React.ReactNode>}
 */
function renderInline(text) {
  const nodes = [];
  let remaining = text || "";

  const patterns = [
    { type: "link", regex: /\[([^\]]+)\]\(([^)]+)\)/ },
    { type: "bold", regex: /\*\*([^*]+)\*\*/ },
    { type: "italic", regex: /\*([^*]+)\*/ },
  ];

  const isSafeHref = (href) => {
    const h = (href || "").trim();
    return (
      h.startsWith("https://") ||
      h.startsWith("http://") ||
      h.startsWith("mailto:") ||
      h.startsWith("tel:") ||
      h.startsWith("#")
    );
  };

  while (remaining.length > 0) {
    let best = null;

    for (const p of patterns) {
      const match = p.regex.exec(remaining);
      if (!match) continue;

      const index = match.index;
      if (!best || index < best.index) {
        best = { ...p, match, index };
      }
    }

    if (!best) {
      nodes.push(remaining);
      break;
    }

    if (best.index > 0) {
      nodes.push(remaining.slice(0, best.index));
    }

    if (best.type === "link") {
      const label = best.match[1] || "";
      const href = best.match[2] || "";
      if (isSafeHref(href)) {
        nodes.push(
          <a
            key={`${label}-${href}-${nodes.length}`}
            href={href}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noreferrer" : undefined}
          >
            {label}
          </a>
        );
      } else {
        nodes.push(label);
      }
    } else if (best.type === "bold") {
      nodes.push(<strong key={`b-${nodes.length}`}>{best.match[1] || ""}</strong>);
    } else if (best.type === "italic") {
      nodes.push(<em key={`i-${nodes.length}`}>{best.match[1] || ""}</em>);
    }

    remaining = remaining.slice(best.index + best.match[0].length);
  }

  return nodes;
}

/**
 * Parses a subset of markdown used in your blog posts into React blocks.
 * Supported:
 * - `##` and `###` headings
 * - paragraphs
 * - `---` horizontal rules
 * - unordered lists (`- item`)
 * - ordered lists (`1. item`)
 * - inline **bold**, *italics*, and [links](url)
 *
 * @param {string} md
 * @returns {Array<React.ReactNode>}
 */
function renderMarkdown(md) {
  const lines = (md || "").split(/\r?\n/);
  const blocks = [];

  let i = 0;
  while (i < lines.length) {
    const raw = lines[i] || "";
    const line = raw.trim();

    // Skip empty lines.
    if (!line) {
      i += 1;
      continue;
    }

    // Skip the top-level title + hero image (we render those separately).
    if (line.startsWith("# ")) {
      i += 1;
      continue;
    }
    if (line.startsWith("![")) {
      i += 1;
      continue;
    }
    if (line.toLowerCase().startsWith("*image prompt:")) {
      i += 1;
      continue;
    }

    if (line === "---") {
      blocks.push(<hr key={`hr-${i}`} />);
      i += 1;
      continue;
    }

    if (line.startsWith("### ")) {
      blocks.push(<h4 key={`h4-${i}`}>{renderInline(line.slice(4))}</h4>);
      i += 1;
      continue;
    }

    if (line.startsWith("## ")) {
      blocks.push(<h3 key={`h3-${i}`}>{renderInline(line.slice(3))}</h3>);
      i += 1;
      continue;
    }

    // Unordered list.
    if (line.startsWith("- ")) {
      const items = [];
      while (i < lines.length && (lines[i] || "").trim().startsWith("- ")) {
        items.push((lines[i] || "").trim().slice(2));
        i += 1;
      }
      blocks.push(
        <ul key={`ul-${i}`}>
          {items.map((t, idx) => (
            <li key={`uli-${idx}`}>{renderInline(t)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list.
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test((lines[i] || "").trim())) {
        items.push((lines[i] || "").trim().replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ol key={`ol-${i}`}>
          {items.map((t, idx) => (
            <li key={`oli-${idx}`}>{renderInline(t)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Paragraph: gather consecutive non-empty lines until a "block starter".
    const paragraphLines = [line];
    i += 1;
    while (i < lines.length) {
      const next = (lines[i] || "").trim();
      if (!next) break;
      if (next === "---") break;
      if (next.startsWith("#")) break;
      if (next.startsWith("![")) break;
      if (next.startsWith("- ")) break;
      if (/^\d+\.\s+/.test(next)) break;
      if (next.toLowerCase().startsWith("*image prompt:")) break;
      paragraphLines.push(next);
      i += 1;
    }
    blocks.push(<p key={`p-${i}`}>{renderInline(paragraphLines.join(" "))}</p>);
  }

  return blocks;
}

/**
 * @typedef {Object} BlogPostMeta
 * @property {number} order
 * @property {string} slug
 * @property {string} title
 * @property {string} excerpt
 * @property {string} mdPath
 * @property {string|null} imagePath
 */

/**
 * Blog component.
 *
 * Variants:
 * - `teaser`: show 3 randomly-selected posts in a horizontal scroll strip with a "View all posts" button.
 * - `full`: show full list + in-page detail view.
 *
 * @param {{ variant?: "teaser" | "full" }} props
 */
const Blog = ({ variant = "full" }) => {
  const [posts, setPosts] = useState(/** @type {Array<BlogPostMeta>} */ ([]));
  const [status, setStatus] = useState({ loading: true, error: "" });
  const [selectedSlug, setSelectedSlug] = useState("");
  const [selectedMd, setSelectedMd] = useState("");

  const selectedPost = useMemo(() => {
    return posts.find((p) => p.slug === selectedSlug) || null;
  }, [posts, selectedSlug]);

  const teaserPosts = useMemo(() => {
    if (variant !== "teaser") return [];
    if (!posts.length) return [];

    // Random but stable for the lifetime of this mounted component.
    const shuffled = [...posts];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, 3);
  }, [posts, variant]);

  // Load manifest.
  useEffect(() => {
    let isMounted = true;

    async function loadManifest() {
      try {
        setStatus({ loading: true, error: "" });
        const res = await fetch("/blog/posts.json", { cache: "no-cache" });
        if (!res.ok) {
          throw new Error(`Failed to load blog manifest (HTTP ${res.status})`);
        }
        const data = await res.json();
        if (!Array.isArray(data)) {
          throw new Error("Blog manifest is not an array.");
        }

        const cleaned = data
          .filter((p) => p && typeof p.slug === "string" && typeof p.title === "string")
          .map((p) => ({
            order: Number(p.order || 0),
            slug: String(p.slug),
            title: String(p.title),
            excerpt: typeof p.excerpt === "string" ? p.excerpt : "",
            mdPath: String(p.mdPath || ""),
            imagePath: p.imagePath ? String(p.imagePath) : null,
          }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        if (!isMounted) return;
        setPosts(cleaned);
        setStatus({ loading: false, error: "" });

        if (variant === "full") {
          // Deep-link support: `#blog/<slug>`
          const hash = window.location.hash || "";
          const match = hash.match(/^#blog\/(.+)$/);
          if (match && match[1]) {
            const slug = decodeURIComponent(match[1]);
            setSelectedSlug(slug);
          }
        }
      } catch (err) {
        if (!isMounted) return;
        setStatus({ loading: false, error: err instanceof Error ? err.message : "Unknown error." });
      }
    }

    loadManifest();
    return () => {
      isMounted = false;
    };
  }, [variant]);

  // Load markdown for selected post.
  useEffect(() => {
    let isMounted = true;

    async function loadPostMd() {
      if (variant !== "full") {
        setSelectedMd("");
        return;
      }

      if (!selectedPost) {
        setSelectedMd("");
        return;
      }

      try {
        setSelectedMd("");
        const res = await fetch(selectedPost.mdPath, { cache: "no-cache" });
        if (!res.ok) {
          throw new Error(`Failed to load blog post (HTTP ${res.status})`);
        }
        const md = await res.text();
        if (!isMounted) return;
        setSelectedMd(md);
      } catch (err) {
        if (!isMounted) return;
        setSelectedMd(`# ${selectedPost.title}\n\nFailed to load this post.\n\n---\n\nPlease try again later.`);
      }
    }

    loadPostMd();
    return () => {
      isMounted = false;
    };
  }, [selectedPost, variant]);

  /**
   * Opens a post in the detail view and updates the URL hash for shareability.
   * @param {string} slug
   */
  const openPost = (slug) => {
    if (variant === "teaser") {
      // Teaser lives on the homepage; open the static, indexable blog post page.
      window.location.assign(`/blog/${encodeURIComponent(slug)}/`);
      return;
    }

    setSelectedSlug(slug);
    window.location.hash = `blog/${encodeURIComponent(slug)}`;

    const section = document.getElementById("blog");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /**
   * Returns back to the blog list and restores the `#blog` anchor.
   */
  const goBackToList = () => {
    setSelectedSlug("");
    setSelectedMd("");
    window.location.hash = "blog";
  };

  return (
    <BlogSection id={variant === "full" ? "blog" : "blog-teaser"}>
      <SectionContainer>
        {variant === "teaser" ? (
          <TeaserHeaderRow>
            <TeaserTitleWrap>
              <SectionTitle>From Our Blog</SectionTitle>
              <SectionSubtitle>
                A few helpful reads from our team. Explore more dental tips, guides, and patient education in the
                full blog.
              </SectionSubtitle>
            </TeaserTitleWrap>
            <ViewAllLink href="/blog/">View all posts</ViewAllLink>
          </TeaserHeaderRow>
        ) : (
          <>
            <SectionTitle>Blog</SectionTitle>
            <SectionSubtitle>
              Practical tips, patient education, and dental insights from Finch & Dudley Dental — all in the same
              calm, patient-first spirit you’ll find in our clinic.
            </SectionSubtitle>
          </>
        )}

        {status.loading ? <StatusText>Loading blog posts…</StatusText> : null}
        {!status.loading && status.error ? <StatusText>{status.error}</StatusText> : null}

        {!status.loading && !status.error && variant === "full" && selectedPost ? (
          <DetailShell>
            <DetailHero>
              {selectedPost.imagePath && <img src={selectedPost.imagePath} alt={selectedPost.title} loading="lazy" />}
            </DetailHero>
            <DetailHeader>
              <BackButton type="button" onClick={goBackToList}>
                {"← Back to all posts"}
              </BackButton>
              <DetailTitle>{selectedPost.title}</DetailTitle>
            </DetailHeader>
            <DetailBody>{renderMarkdown(selectedMd)}</DetailBody>
          </DetailShell>
        ) : null}

        {!status.loading && !status.error && variant === "teaser" ? (
          <HorizontalScroll aria-label="Featured blog posts">
            {teaserPosts.map((post) => (
              <TeaserCard key={post.slug} type="button" onClick={() => openPost(post.slug)} aria-label={post.title}>
                <CardImage>
                  {post.imagePath && <img src={post.imagePath} alt={post.title} loading="lazy" />}
                </CardImage>
                <CardBody>
                  <CardTitle>{post.title}</CardTitle>
                  <CardExcerpt>{post.excerpt}</CardExcerpt>
                  <CardCta>{"Read more →"}</CardCta>
                </CardBody>
              </TeaserCard>
            ))}
          </HorizontalScroll>
        ) : null}

        {!status.loading && !status.error && variant === "full" && !selectedPost ? (
          <Grid>
            {posts.map((post) => (
              <Card key={post.slug} type="button" onClick={() => openPost(post.slug)} aria-label={post.title}>
                <CardImage>
                  {post.imagePath && <img src={post.imagePath} alt={post.title} loading="lazy" />}
                </CardImage>
                <CardBody>
                  <CardTitle>{post.title}</CardTitle>
                  <CardExcerpt>{post.excerpt}</CardExcerpt>
                  <CardCta>{"Read more →"}</CardCta>
                </CardBody>
              </Card>
            ))}
          </Grid>
        ) : null}
      </SectionContainer>
    </BlogSection>
  );
};

export default Blog;

